import { Component, createRef } from 'react';
import styles from './ClientStyles.module.scss';

// Letter-slot answer input for 'exact' answer mode.
//
// Layout (top → bottom):
//   1. Cell preview row — read-only visualization of word lengths, filled as the user types.
//   2. Real visible <input> — what the user actually clicks and types into.
//   3. Submit button.
//
// A real visible input is used (rather than an overlay over the cells) because mobile
// browsers will only open the soft keyboard when a *truly visible, focusable, enabled*
// input is focused inside a user-gesture handler — anything fancier breaks in at least
// one browser.

const isAlnum = (ch) => /[a-z0-9]/i.test(ch);

// Unique id per LetterSlotInput instance so multiple coexisting instances (shouldn't happen,
// but defensive) don't end up with the same label-for-input association.
let _slotInputId = 0;

class LetterSlotInput extends Component {
    constructor(props) {
        super(props);
        this.state = { typed: "" };
        this.inputRef = createRef();
        this.inputId = `slotInput_${++_slotInputId}`;
    }

    componentDidMount() {
        // Expose .focus() to the parent so it can be called from the buzz user-gesture handler.
        // Skipped in hintOnly mode — the slot field is purely visual there, no input.
        if (typeof this.props.registerFocus === 'function' && !this.props.hintOnly) {
            this.props.registerFocus(() => this.inputRef.current?.focus());
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.skeleton !== this.props.skeleton) {
            this.setState({ typed: "" });
        }
        // If the player's buzz times out (or otherwise loses buzz), explicitly blur the input
        // so any browser-side focus styling drops away too. Belt-and-braces alongside the
        // CSS that gates :focus styling on .letterSlotBuzzed.
        if (prevProps.buzzed && !this.props.buzzed && this.inputRef.current) {
            this.inputRef.current.blur();
            this.setState({ typed: "" });
        }
    }

    maxLen() {
        // Count fillable positions — both alphanumerics (rare in a skeleton, but defensive)
        // and underscore placeholders. The server emits the skeleton as e.g. "_____ _____",
        // so without the underscore case this returned 0 and silently rejected all typing.
        return (this.props.skeleton || "").split("").filter(ch => isAlnum(ch) || ch === "_").length;
    }

    handleChange = (e) => {
        // Only accept typing once buzzed in. Before that, the input is enabled (so it stays
        // focusable for the buzz-click auto-focus) but ignores keystrokes here.
        if (!this.props.buzzed) return;
        const raw = e.target.value || "";
        const cleaned = raw.replace(/[^a-z0-9]/gi, "");
        const truncated = cleaned.slice(0, this.maxLen());
        this.setState({ typed: truncated });
    }

    handleSubmit = (e) => {
        e?.preventDefault();
        const { typed } = this.state;
        if (!typed) return;
        const skeleton = this.props.skeleton || "";
        let idx = 0;
        let reconstructed = "";
        for (const ch of skeleton) {
            if (isAlnum(ch) || ch === "_") {
                reconstructed += typed[idx] || "_";
                if (typed[idx]) idx++;
            } else {
                reconstructed += ch;
            }
        }
        this.props.onSubmit?.(reconstructed.trim());
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') this.handleSubmit(e);
    }

    renderSlots() {
        const skeleton = this.props.skeleton || "";
        const typed = this.state.typed;
        const cursorIdx = typed.length; // next slot to fill
        let alnumIdx = 0;
        const out = [];
        let wordKey = 0;
        let currentWord = [];
        const flushWord = () => {
            if (currentWord.length) {
                out.push(<div className={styles.slotWord} key={`w${wordKey++}`}>{currentWord}</div>);
                currentWord = [];
            }
        };
        for (let i = 0; i < skeleton.length; i++) {
            const ch = skeleton[i];
            const isFillable = isAlnum(ch) || ch === "_";
            if (ch === " ") {
                flushWord();
            } else if (isFillable) {
                const letter = typed[alnumIdx];
                const isCurrent = alnumIdx === cursorIdx;
                currentWord.push(
                    <div
                        className={`${styles.slot} ${letter ? styles.slotFilled : ''} ${isCurrent ? styles.slotCurrent : ''}`}
                        key={`s${i}`}
                    >
                        {letter ? letter.toUpperCase() : ""}
                    </div>
                );
                alnumIdx++;
            } else {
                currentWord.push(<div className={styles.slotPunct} key={`p${i}`}>{ch}</div>);
            }
        }
        flushWord();
        return out;
    }

    render() {
        const { buzzed, hintOnly, onGiveUp, renderTextDiv } = this.props;
        const totalSlots = this.maxLen();
        const filled = this.state.typed.length;
        // Submit is enabled once buzzed in and all slots filled. Don't gate on lockedOut —
        // that flag includes the local buzzingIn state which is true for the player who's
        // actively answering (the very player who needs to submit).
        const canSubmit = buzzed && filled === totalSlots && totalSlots > 0;

        // hintOnly = legacy prop kept as a non-interactive override. No live caller; the
        // path stays in place in case a hint-style mode comes back.
        // Treat the visual state as "not buzzed" regardless of the buzzed prop.
        const visuallyBuzzed = buzzed && !hintOnly;

        return (
            <div className={`${styles.letterSlotInput} ${visuallyBuzzed ? styles.letterSlotBuzzed : ''} ${hintOnly ? styles.letterSlotHintOnly : ''}`}>
                {/* Input first in DOM so a CSS sibling selector can target the label when
                    the input has focus. Always enabled (so .focus() works inside the buzz
                    click's user-gesture context — disabled inputs can't be focused, breaking
                    mobile keyboard pop). Typing is ignored in handleChange until buzzed. */}
                <input
                    id={this.inputId}
                    ref={this.inputRef}
                    className={styles.slotsRealInput}
                    type="text"
                    value={this.state.typed}
                    onChange={this.handleChange}
                    onKeyDown={this.handleKeyDown}
                    autoComplete="off"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="text"
                    aria-label="Type your answer"
                />

                {/* Label association is conditional on buzz state — when not buzzed in, the
                    label has no htmlFor, so clicking cells doesn't focus the input (and the
                    keyboard doesn't pop). On buzz, the association is added and the buzz
                    handler programmatically focuses the input inside the user gesture. */}
                <label htmlFor={buzzed ? this.inputId : undefined} className={styles.slotsField}>
                    <div className={styles.slotsRow}>{this.renderSlots()}</div>
                </label>

                {buzzed && !hintOnly && (
                    <div className={styles.slotsActionRow}>
                        <button
                            className={`${styles.mainButton} ${styles.smaller} ${styles.grey} ${styles.slotsGiveUp}`}
                            onClick={onGiveUp}
                        >
                            {renderTextDiv ? renderTextDiv('Give Up') : 'Give Up'}
                        </button>
                        <button
                            className={`${styles.mainButton} ${styles.smaller} ${styles.slotsSubmit}`}
                            onClick={this.handleSubmit}
                            disabled={!canSubmit}
                        >
                            {renderTextDiv ? renderTextDiv('Submit') : 'Submit'}
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

export default LetterSlotInput;
